import { getMimeTypeFromName } from "components/job/files/fileHelpers";
import { FileInputType, FileType } from "operations/schema/schema";
import Resizer from "react-image-file-resizer";

export const formatBytes = (bytes: number, decimals: number = 2) => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

// Convert a raw base64 string into a Blob of the given MIME type.
const base64ToBlob = (base64: string, mimeType: string): Blob => {
  const byteChars = atob(base64);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    byteNumbers[i] = byteChars.charCodeAt(i);
  }
  return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
};

// iPadOS 13+ reports itself as "MacIntel", so distinguish it by touch support.
const isIos = () => {
  const ua = navigator.userAgent;
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
};

export const downloadFile = (file: FileInputType | FileType) => {
  const raw = file.data ?? "";
  if (!raw) return;

  // iOS Safari shows a download prompt for data: URLs but never actually saves the
  // file. Build a Blob instead: a blob: URL completes the same native prompt, and in
  // an installed PWA (no download manager) we fall back to the native share sheet.
  // Non-iOS devices (desktop, Android) keep the original data: URL path untouched.
  if (isIos()) {
    let mimeType = getMimeTypeFromName(file.name);
    let base64 = raw;
    if (raw.startsWith("data:")) {
      const match = raw.match(/^data:([^;]+);base64,(.*)$/);
      if (match) {
        mimeType = match[1];
        base64 = match[2];
      }
    }
    const blob = base64ToBlob(base64, mimeType);

    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (navigator as any).standalone === true;
    const shareFile = new File([blob], file.name, { type: mimeType });
    if (isStandalone && navigator.canShare?.({ files: [shareFile] })) {
      navigator.share({ files: [shareFile] }).catch(() => {
        /* user cancelled the share sheet */
      });
      return;
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = file.name;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return;
  }

  // Original path — unchanged for desktop and Android.
  const link = document.createElement("a");
  link.download = file.name;
  // Files may be stored either as a raw base64 string (work files) or as a full data: URL
  // (visit/system files). Use the data: URL directly; otherwise wrap the raw base64.
  link.href = raw.startsWith("data:")
    ? raw
    : `data:application/octet-stream;base64,${raw}`;
  link.click();
};

export const resizeUploadFile = (file: any) => {
  return new Promise((resolve) => {
    if (file.type.includes("image/")) {
      Resizer.imageFileResizer(
        file,
        800,
        800,
        "JPEG",
        80,
        0,
        (uri) => {
          resolve(uri);
        },
        "base64"
      );
    } else {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result);
      };
      reader.readAsDataURL(file);
    }
  });
};
